import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { debugZodResolver } from '@/lib/debugZodResolver';

import useCreateUserWithGoogle from '@/features/auth/signup/hooks/useCreateUserWithGoogle.ts';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer.tsx';
import EmailInput from '@/components/form/EmailInput.tsx';
import PasswordInput from '@/components/form/PasswordInput.tsx';
import useLogin from '@/features/auth/login/hooks/useLogin.ts';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  LoginFormData,
  loginSchema,
} from '@/features/auth/signup/schemas/loginSchema.ts';
import { loginRoute } from '@/features/auth/login/routes/loginRoute.ts';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import GoogleIcon from '@/components/svg/GoogleIcon.tsx';
import { readInvitationFromRedirect } from '@/features/public/acceptInvitation/utils/invitationFromRedirect.ts';
import { Description } from '@/components/ui/Title.tsx';

const LoginPage = () => {
  const navigate = useNavigate();
  const { redirect, emailVerified } = loginRoute.useSearch();
  const invitation = readInvitationFromRedirect(redirect);
  const toastShown = useRef(false);

  useEffect(() => {
    if (emailVerified && !toastShown.current) {
      toastShown.current = true;
      toast.success(
        'Twój adres email został zweryfikowany. Zaloguj się aby kontynuować rejestrację.',
      );
    }
  }, [emailVerified]);

  const { mutate: login, isPending: isLoginLoading } = useLogin(redirect);
  const { mutate: createUserWithGoogle, isPending: isLoadingGoogle } =
    useCreateUserWithGoogle(redirect);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: debugZodResolver(loginSchema),
    defaultValues: { email: invitation?.email ?? '' },
  });

  const onSubmit = (data: LoginFormData) => {
    login({
      email: data.email,
      password: data.password,
    });
  };

  const navigateToResetPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    // navigate({to: '/auth/reset-password-request'});
  };

  return (
    <AuthFormContainer title="Zaloguj się">
      {invitation && (
        <Description className="w-full text-center">
          {invitation.companyName
            ? `Dołączasz do firmy ${invitation.companyName}`
            : 'Dołączasz do zespołu'}
        </Description>
      )}
      <div className="flex w-full flex-col items-end gap-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col items-start gap-6"
        >
          <div className="w-full space-y-4">
            <EmailInput
              label="Adres e-mail"
              register={register('email')}
              required={true}
              error={errors.email?.message}
            />

            <div className="space-y-1">
              <PasswordInput
                label="Hasło"
                register={register('password')}
                required={true}
                error={errors.password?.message}
              />

              <div className="flex w-full items-start justify-end gap-1">
                <span className="text-sm text-neutral-600">
                  Zapomniałeś hasła?
                </span>
                <a
                  href="/auth/reset-password-request"
                  className="text-sm text-info-300 underline"
                  onClick={navigateToResetPassword}
                >
                  Zresetuj hasło
                </a>
              </div>
            </div>
          </div>

          <ActionButton
            type="submit"
            tone="accent"
            size="block"
            isLoading={isLoginLoading}
          >
            Zaloguj się
          </ActionButton>
        </form>

        <div className="flex w-full flex-col items-center gap-4">
          <div className="w-full text-center text-sm font-normal leading-[22.4px] text-base-text">
            lub
          </div>
          <ActionButton
            tone="outline"
            size="block"
            onClick={() => createUserWithGoogle()}
            disabled={isLoadingGoogle}
          >
            <GoogleIcon /> Kontynuuj z Google
          </ActionButton>
        </div>

        <div className="flex w-full items-start justify-center gap-2">
          <span className="text-center text-sm text-base-text">
            Nie masz konta?
          </span>
          <a
            href="/auth/signup"
            className="text-sm text-info-300 underline"
            onClick={(e) => {
              e.preventDefault();
              navigate({
                to: '/auth/signup',
                search: redirect ? { redirect } : {},
              });
            }}
          >
            Zarejestruj się
          </a>
        </div>
      </div>
    </AuthFormContainer>
  );
};

export default LoginPage;
